<template>
  <div>
    <div>
      <h1 class="text-4xl pb-4 pl-2 font-extrabold tracking-tight text-gray-900">{{ t("title") }}</h1>
    </div>

    <Alert v-if="blockers?.paymentsNotUpToDate" type="error" class="mb-6">
      {{ t("alert_payments_line1") }}
      <br />
      <span v-html="paymentsAlertHtml"></span>
    </Alert>
    <Alert v-if="blockers?.withinCommitmentPeriod" type="info" class="mb-6">
      <span v-html="commitmentAlertHtml"></span>
    </Alert>
    <Alert v-if="blockers?.hasApprovedClaim" type="error" class="mb-6">
      <span v-html="approvedClaimAlertHtml"></span>
    </Alert>

    <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
      <form @submit="handleSubmit($event, cancel)" novalidate class="space-y-6">
        <div>
          <label for="cancellationDate" class="block text-sm leading-6 text-gray-900 sm:pt-1.5 mb-1 font-semibold">
            {{ t("Cancellation date") }}
          </label>
          <Field
            id="cancellationDate"
            name="cancellationDate"
            type="date"
            v-model="cancellationDate"
            :min="minDate"
            class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
          />
          <ErrorMessage name="cancellationDate" class="mt-1 text-sm text-red-600" />
        </div>

        <Field name="selectedReason" v-model="selectedReason" v-slot="{ errors: reasonErrors }">
          <Listbox as="div" v-model="selectedReason">
            <ListboxLabel
              for="cancellationReason"
              class="block text-sm leading-6 text-gray-900 sm:pt-1.5 mb-1 font-semibold"
            >
              {{ t("Reason") }}
            </ListboxLabel>
            <div class="relative">
              <ListboxButton
                :class="[
                  'rounded-lg relative cursor-pointer border bg-white py-2 pl-3 text-left focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary sm:text-sm w-full',
                  reasonErrors.length ? 'border-red-500' : '',
                ]"
              >
                <div class="items-center">
                  <div>
                    <span class="font-medium text-primary" v-if="selectedReason">
                      {{ t("Plan cancellation reasons." + selectedReason.reason) }}
                    </span>
                    <span class="font-medium text-primary" v-else>
                      {{ t("Please select an option in the dropdown below") }}
                    </span>
                    <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </ListboxButton>

              <transition
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm cursor-pointer"
                >
                  <ListboxOption
                    as="template"
                    v-for="reason in reasons"
                    :key="reason.id"
                    :value="reason"
                    v-slot="{ active, selected }"
                  >
                    <li
                      :class="[
                        active ? 'text-button-font-primary bg-primary' : 'text-gray-900',
                        'relative select-none py-2 pl-3 pr-9 cursor-pointer',
                      ]"
                    >
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate cursor-pointer']">
                        {{ t("Plan cancellation reasons." + reason.reason) }}
                      </span>

                      <span
                        v-if="selected"
                        :class="[
                          active ? 'text-button-font-primary bg-primary' : 'text-gray-900',
                          'absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer',
                        ]"
                      >
                        <CheckIcon class="h-5 w-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
          <ErrorMessage name="selectedReason" class="mt-1 text-sm text-red-600" />
        </Field>

        <Field name="selectedFeedback" v-model="selectedFeedback" v-slot="{ errors: feedbackErrors }">
          <div>
            <h3 class="block text-sm leading-6 text-gray-900 sm:pt-1.5 mb-1 font-semibold">
              {{ t("How would you rate your Estaly experience?") }}
            </h3>
            <div class="grid grid-cols-1 gap-y-6 sm:grid-cols-4 sm:gap-x-4">
              <button
                type="button"
                v-for="feedback in feedbackLists"
                :key="feedback.id"
                @click="selectedFeedback = feedback"
                :class="[
                  selectedFeedback?.id === feedback.id
                    ? 'border-primary bg-primary-50'
                    : feedbackErrors.length
                      ? 'border-red-300'
                      : 'border-gray-300',
                  'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-xs hover:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2',
                ]"
              >
                <span class="flex flex-1">
                  <span class="flex flex-col">
                    <span class="block text-sm font-medium text-gray-900">
                      {{ t("feedback_" + feedback.id) }}
                    </span>
                  </span>
                </span>
              </button>
            </div>
            <ErrorMessage name="selectedFeedback" class="mt-1 text-sm text-red-600" />
          </div>
        </Field>

        <div>
          <label for="feedbackMessage" class="block text-sm leading-6 text-gray-900 sm:pt-1.5 mb-1 font-semibold">
            {{ t("Can you tell us a little more about it?") }}
          </label>
          <div class="mt-1">
            <Field
              as="textarea"
              rows="4"
              name="feedbackMessage"
              id="feedbackMessage"
              v-model="feedbackMessage"
              class="block w-full rounded-md border-gray-300 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
            />
          </div>
          <ErrorMessage name="feedbackMessage" class="mt-1 text-sm text-red-600" />
        </div>

        <div class="mt-8 flex justify-end">
          <GenericButton
            data-test="cancellation-submit"
            :disabled="isBlocked"
            class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium focus:outline-hidden text-button-font-primary bg-primary uppercase"
          >
            {{ t("submit_button") }}
          </GenericButton>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script>
import { Alert, GenericButton } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { CheckIcon, ChevronDownIcon } from "@heroicons/vue/outline";
import { formatDate, parseDate } from "@/utils/date_formatter";
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import { useI18n } from "vue-i18n";
import DOMPurify from "dompurify";

export default {
  components: {
    Alert,
    GenericButton,
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    CheckIcon,
    ChevronDownIcon,
    VeeForm,
    Field,
    ErrorMessage,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    plan: {
      type: Object,
      required: true,
    },
  },

  async created() {
    this.reasons = this.formattedCancellationReasons();
  },

  data() {
    return {
      cancellationDate: "",
      selectedReason: null,
      selectedFeedback: null,
      feedbackMessage: "",
      reasons: [],
      feedbackLists: [
        { id: 1, title: "Super" },
        { id: 2, title: "Bien" },
        { id: 3, title: "Passable" },
        { id: 4, title: "Mauvaise" },
      ],
    };
  },

  computed: {
    ...mapGetters("cancellations", ["clientReasons"]),
    // Les valeurs interpolées sont nos propres traductions et des dates formatées :
    // le <strong> est injecté ici pour garder la phrase traduisible d'un seul tenant.
    paymentsAlertHtml() {
      return this.sanitizedAlert("alert_payments_line2", { tab: this.t("invoices") });
    },
    commitmentAlertHtml() {
      return this.sanitizedAlert("alert_commitment", {
        commitment: this.t("commitment_duration"),
        date: this.formatDate(this.firstPossibleCancellationDate, this.$i18n.locale),
      });
    },
    approvedClaimAlertHtml() {
      return this.sanitizedAlert("alert_approved_claim", {
        claim: this.t("approved_claim"),
        date: this.formatDate(this.endOfCurrentContractYear, this.$i18n.locale),
      });
    },
    blockers() {
      return this.plan.cancellationBlockers;
    },
    // Un sinistre approuvé ne bloque pas la résiliation : il repousse la date minimale (cf. minDate).
    isBlocked() {
      return Boolean(this.blockers?.paymentsNotUpToDate);
    },
    firstPossibleCancellationDate() {
      if (!this.plan.date) return null;
      return this.addYears(this.parseDate(this.plan.date), 1);
    },
    endOfCurrentContractYear() {
      if (!this.plan.date) return null;
      const start = this.parseDate(this.plan.date);
      const today = new Date();
      let end = this.addYears(start, 1);
      while (today >= end) end = this.addYears(end, 1);
      return end;
    },
    minDate() {
      let min = new Date();
      if (this.blockers?.withinCommitmentPeriod && this.firstPossibleCancellationDate > min) {
        min = this.firstPossibleCancellationDate;
      }
      if (this.blockers?.hasApprovedClaim && this.endOfCurrentContractYear > min) {
        min = this.endOfCurrentContractYear;
      }
      const d = new Date(min);
      d.setHours(12, 0, 0, 0);
      return d.toISOString().split("T")[0];
    },
    schema() {
      return yup.object({
        cancellationDate: yup.string().required(this.t("cancellation_date_required")),
        selectedReason: yup.mixed().required(this.t("reason_required")),
        selectedFeedback: yup.mixed().required(this.t("experience_required")),
        feedbackMessage: yup.string().required(this.t("message_required")),
      });
    },
  },

  methods: {
    sanitizedAlert(key, values) {
      const bold = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, `<strong>${v}</strong>`]));
      return DOMPurify.sanitize(this.t(key, bold));
    },
    ...mapActions("notifications", ["notify"]),
    ...mapActions("cancellations", ["cancelPlan", "scheduleCancellation"]),
    formatDate,
    parseDate,

    addYears(date, n) {
      const d = new Date(date);
      d.setFullYear(d.getFullYear() + n);
      return d;
    },

    async cancel() {
      if (this.isBlocked) return;

      const todayStr = new Date().toISOString().split("T")[0];
      const isImmediateCancellation = !this.blockers?.withinCommitmentPeriod && this.cancellationDate === todayStr;

      if (isImmediateCancellation) {
        const params = {
          cancellationDate: this.cancellationDate,
          cancellationReason: this.selectedReason?.reason,
          feedbackExperience: this.selectedFeedback?.title,
          feedbackMessage: this.feedbackMessage,
        };
        const { success } = await this.cancelPlan({ planFriendlyId: this.plan.friendlyId, params });
        if (!success) return;
      } else {
        const params = {
          planSaleId: this.plan.id,
          scheduledCancellationDate: this.cancellationDate,
          cancellationReason: this.selectedReason?.reason,
          feedbackExperience: this.selectedFeedback?.title,
          feedbackMessage: this.feedbackMessage,
        };
        const { success } = await this.scheduleCancellation({ params });
        if (!success) return;
      }

      this.notify({
        type: "success",
        category: "simple",
        title: this.t("success_title"),
        text: isImmediateCancellation ? this.t("cancel_success") : this.t("schedule_success"),
      });

      this.$router.push("/plans");
    },

    formattedCancellationReasons() {
      return this.clientReasons.map((reason, i) => ({ id: i, reason }));
    },
  },
};
</script>

<i18n src="./CancellationFlow.i18n.json"></i18n>
